import moment from 'moment'

import { app } from '../app.module'

interface IOptions {
  startEndDate: {
    startDate: number
    endDate: number
  }
  categoryName: string
  subCategoryFilter: string
  accountFilter: string
  postTypeFilter: string
  messageFilter: string
  tagFilter: string
  hashtagFilter: string
  followerFilter: number
  favoriteFilter: string
  accountGlobalFilter: number
  groupId: number
  limit: number
  order: string
  page: number
  postsCount: number
}

// 投稿のアカウントデータ表示のインタフェース
interface IPostAccountView {
  accountId: string
  screenName: string
  accountUrl: string
  pictureUrl: string
  followers: number
  categoryName: string
  subCategoryName: string
  snsType: string
  dataFlg: number
  hiddenSubscribersFlg?: number
  postDateFollowers: number
}

// タグ表示のインタフェース
interface ITagView {
  globalTagIds: string
  globalTagNames: string
  groupTagIds: string
  groupTagNames: string
  userTagIds: string
  userTagNames: string
}

// 投稿表示のインタフェース
export interface IPostView {
  postId: string
  account: IPostAccountView
  postUrl: string
  publishDate: string
  publishDateUnix: number
  type?: string
  message: string
  picture?: [
    {
      url: string
    },
  ]
  likes: number
  comments: number
  tags?: ITagView
  hashtags?: string[]
  memoCount: number
}

// Youtubeの投稿表示のインタフェース
interface IYtPostView extends IPostView {
  title: string
  views: number
  dislikes: number
  duration: number
}

// DBから投稿データのインタフェース
interface IYtPostData {
  ytPostId: string
  accountId: string
  accountCategoryName: string
  accountFollowers: number
  accountGlobalTagNames: string
  accountGroupTagNames: string
  accountPictureUrl: string
  accountScreenName: string
  accountSubCategoryName: string
  accountUserTagNames: string
  userTagIds: string
  userTagNames: string
  dataFlg: number
  hiddenSubscribersFlg: number
  globalTagIds: string
  globalTagNames: string
  groupTagIds: string
  groupTagNames: string
  // hashtagName: string;
  title: string
  publishDate: string
  description: string
  thumbnailUrl: string
  views: number
  likes: number
  dislikes: number
  duration: number
  comments: number
  postDateFollowers: number
  memoCount: number
}

// Lineの投稿表示
interface ILinePostView extends IPostView {
  shares: number
  engagements: number
  engagementRate: number
  totalMedia: number
}
// Lineの投稿データ
interface ILinePostData {
  linePostId: string
  accountId: string
  accountCategoryName: string
  accountFollowers: number
  accountPictureUrl: string
  accountScreenName: string
  accountSubCategoryName: string
  dataFlg: number
  type: string
  message: string
  publishDate: string
  thumbnailUrl: string
  likes: number
  comments: number
  shares: number
  engagements: number
  engagementRate: number
  postDateFollowers: number
  totalMedia: number
  userTagIds: string
  userTagNames: string
  globalTagIds: string
  globalTagNames: string
  groupTagIds: string
  groupTagNames: string
  memoCount: number
}

// TikTokの投稿表示
interface ITikTokPostView extends IPostView {
  shares: number
  collects: number
  plays: number
  engagements: number
  engagementRate: number
  totalMedia: number
  music: {
    id: string
    title: string
    authorName: string
    thumbnailUrl: string
    url: string
  }
}
// TikTokの投稿データ
interface ITikTokPostData {
  ttPostId: string
  accountId: string
  accountCategoryName: string
  accountFollowers: number
  accountPictureUrl: string
  accountScreenName: string
  accountUsername: string
  accountSubCategoryName: string
  dataFlg: number
  type: string
  message: string
  publishDate: string
  thumbnailUrl: string
  likes: number
  comments: number
  shares: number
  collects: number
  plays: number
  engagements: number
  engagementRates: number
  totalMedia: number
  postDateFollowers: number
  userTagIds: string
  userTagNames: string
  globalTagIds: string
  globalTagNames: string
  groupTagIds: string
  groupTagNames: string
  memoCount: number
  ttPostMusicId: string
  musicTitle: string
  musicAuthorName: string
  musicThumbnailUrl: string
}

/*
 * PostAction 投稿用サービス
 */
function PostAction(RestangularService, $q) {
  /**
   * カウントデータを取得(facebook, twitter, instagram, youtube, line, tiktok)
   * @param {string} getType 取得するデータのタイプ
   * @param {string} snsType SNSタイプ
   * @param {IOptions} getOptions 取得のパラメーター
   * @return {Object} カウントデータ
   */
  function getCounts(getType: string, snsType: string, getOptions: IOptions) {
    const deferred = $q.defer()

    RestangularService.request('GET', '/counts', {
      options: JSON.stringify(getOptions),
      snsType: snsType,
      type: getType,
    }).then((postDatas) => {
      switch (getType) {
        case 'getTag':
          this.tagLists = postDatas[0].data
          break

        case 'getAccountTag':
          this.accountTagLists = postDatas[0].data
          break

        case 'getAccount':
          this.accountLists = postDatas[0].data
          break

        case 'getHashtag':
          this.hashtagLists = postDatas[0].data
          break
      }
      deferred.resolve(postDatas[0].data)
    })
    return deferred.promise
  }

  return {
    getCounts: getCounts,
    accountLists: null,
    tagLists: null,
    accountTagLists: null,
    hashtagLists: null,
    hashtagFilterStack: [],
  }
}

/**
 * FbPostSetData Faceebookの投稿のデータセットに関するサービス
 * @return {Object} オブジェクト
 */
function FbPostSetData() {
  /**
   * setFbPosts Facebook投稿のデータをセット
   * @param {Object} fbPostDatas 投稿一覧
   * @return {Object} 投稿一覧
   */
  function setFbPosts(fbPostDatas) {
    // データがない場合、リータン
    if (fbPostDatas.length === 0) {
      return []
    }
    const fbPosts = []

    // 投稿のデータをセット
    fbPostDatas.forEach(function (fbPost) {
      // 投稿のデータをセット
      const fbPostObject = {
        postId: fbPost.fbPostId,
        account: {
          accountId: fbPost.accountId,
          screenName: fbPost.accountScreenName,
          accountUrl: 'https://www.facebook.com/' + fbPost.accountId,
          pictureUrl: fbPost.accountPictureUrl,
          internationalFlg: fbPost.internationalFlg,
          followers: fbPost.accountFollowers,
          postDateFollowers: fbPost.postDateFollowers,
          categoryName: fbPost.accountCategoryName,
          subCategoryName: fbPost.accountSubCategoryName,
          snsType: 'facebook',
          dataFlg: fbPost.dataFlg,
        },
        postUrl: 'https://www.facebook.com/' + fbPost.accountId + '/posts/' + fbPost.fbPostId,
        publishDate: moment(fbPost.publishDate).format('ll HH:mm'),
        publishDateUnix: moment(fbPost.publishDate).unix(),
        type: fbPost.type,
        message: fbPost.message,
        picture: [
          {
            url: fbPost.thumbnailUrl,
          },
        ],
        totalMedia: fbPost.totalMedia,
        likes: fbPost.likes,
        comments: fbPost.comments,
        shares: fbPost.shares,
        engagements: fbPost.engagements,
        engagementRate: fbPost.engagementRate,
        videoViews: fbPost.videoViews,
        tags: {
          globalTagIds: fbPost.globalTagIds,
          globalTagNames: fbPost.globalTagNames,
          groupTagIds: fbPost.groupTagIds,
          groupTagNames: fbPost.groupTagNames,
          userTagIds: fbPost.userTagIds,
          userTagNames: fbPost.userTagNames,
        },
        favoriteGroupId: fbPost.favoriteGroupId,
        memoCount: fbPost.memoCount,
      }

      // 投稿一覧に投稿のデータを追加
      fbPosts.push(fbPostObject)
    })

    return fbPosts
  }

  return {
    setFbPosts: setFbPosts,
  }
}

/**
 * TwPostSetData Twitterの投稿のデータセットに関するサービス
 * @return {Object} オブジェクト
 */
function TwPostSetData() {
  /**
   * setTwPosts Twitter投稿のデータをセット
   * @param {array} twPostDatas 投稿一覧
   * @return {array} 投稿一覧
   */
  function setTwPosts(twPostDatas) {
    // データがない場合、リータン
    if (twPostDatas.length === 0) {
      return []
    }

    const twPosts = []
    twPostDatas.forEach(function (twPost) {
      // 投稿のデータをセット
      const twPostObject = {
        postId: twPost.twPostId,
        account: {
          accountId: twPost.accountId,
          screenName: twPost.accountScreenName,
          username: twPost.accountUsername,
          accountUrl: 'https://x.com/' + twPost.accountUsername,
          pictureUrl: twPost.accountPictureUrl,
          followers: twPost.accountFollowers,
          postDateFollowers: twPost.postDateFollowers,
          categoryName: twPost.accountCategoryName,
          subCategoryName: twPost.accountSubCategoryName,
          snsType: 'twitter',
          dataFlg: twPost.dataFlg,
        },
        postUrl: 'https://x.com/' + twPost.accountId + '/status/' + twPost.twPostId,
        publishDate: moment(twPost.publishDate).format('ll HH:mm'),
        publishDateUnix: moment(twPost.publishDate).unix(),
        type: twPost.type,
        message: twPost.message,
        picture: [
          {
            url: twPost.thumbnailUrl,
          },
        ],
        totalMedia: twPost.totalMedia,
        likes: twPost.likes,
        retweets: twPost.retweets,
        impressions: twPost.impressions,
        replies: twPost.replies,
        quotes: twPost.quotes,
        engagements: twPost.engagements,
        engagementRate: twPost.engagementRate,
        tags: {
          globalTagIds: twPost.globalTagIds,
          globalTagNames: twPost.globalTagNames,
          groupTagIds: twPost.groupTagIds,
          groupTagNames: twPost.groupTagNames,
          userTagIds: twPost.userTagIds,
          userTagNames: twPost.userTagNames,
        },
        favoriteGroupId: twPost.favoriteGroupId,
        memoCount: twPost.memoCount,
      }

      // 投稿一覧に投稿のデータを追加
      twPosts.push(twPostObject)
    })
    return twPosts
  }

  return {
    setTwPosts: setTwPosts,
  }
}

/**
 * InPostSetData Instagramの投稿のデータセットに関するサービス
 * @return {Object} オブジェクト
 */
function InPostSetData() {
  /**
   * setInPosts Instagram投稿のデータをセット
   * @param {Object} inPostDatas 投稿一覧
   * @return {Object} 投稿一覧
   */
  function setInPosts(inPostDatas) {
    // データがない場合、リータン
    if (inPostDatas.length === 0) {
      return []
    }

    const inPosts = []

    // 投稿のデータをセット
    inPostDatas.forEach(function (inPost) {
      // 投稿のデータをセット
      const inPostObject = {
        postId: inPost.inPostId,
        account: {
          accountId: inPost.accountId,
          screenName: inPost.accountScreenName,
          username: inPost.accountUsername,
          accountUrl: 'https://www.instagram.com/' + inPost.accountUsername,
          pictureUrl: inPost.accountPictureUrl,
          followers: inPost.accountFollowers,
          postDateFollowers: inPost.postDateFollowers,
          categoryName: inPost.accountCategoryName,
          subCategoryName: inPost.accountSubCategoryName,
          snsType: 'instagram',
          dataFlg: inPost.dataFlg,
        },
        postUrl: inPost.linkUrl,
        publishDate: moment(inPost.publishDate).format('ll HH:mm'),
        publishDateUnix: moment(inPost.publishDate).unix(),
        type: inPost.type,
        message: inPost.message,
        thumbnailUrl: inPost.thumbnailUrl,
        picture: [
          {
            url: inPost.thumbnailUrl,
          },
        ],
        totalMedia: inPost.totalMedia,
        likes: inPost.likes,
        comments: inPost.comments,
        engagements: inPost.engagements,
        engagementRate: inPost.engagementRate,
        videoViews: inPost.videoViews,
        tags: {
          globalTagIds: inPost.globalTagIds,
          globalTagNames: inPost.globalTagNames,
          groupTagIds: inPost.groupTagIds,
          groupTagNames: inPost.groupTagNames,
          userTagIds: inPost.userTagIds,
          userTagNames: inPost.userTagNames,
        },
        favoriteGroupId: inPost.favoriteGroupId,
        memoCount: inPost.memoCount,
      }

      // 投稿一覧に投稿のデータを追加
      inPosts.push(inPostObject)
    })

    return inPosts
  }

  return {
    setInPosts: setInPosts,
  }
}

/**
 * YtPostSetData Youtubeの投稿のデータセットに関するサービス
 * @return {Object} オブジェクト
 */
function YtPostSetData() {
  /**
   * Youtube投稿のデータをセット
   * @param {Object} ytPostDatas 投稿一覧
   * @return {Object} 投稿一覧
   */
  function setYtPosts(ytPostDatas) {
    // データがない場合、リータン
    if (ytPostDatas.length === 0) {
      return []
    }

    const ytPosts: IYtPostView[] = []

    const postDatas: IYtPostData[] = ytPostDatas

    // 投稿のデータをセット
    postDatas.forEach(function (ytPost) {
      // 投稿一覧に投稿のデータを追加
      ytPosts.push(setYtPostView(ytPost))
    })

    return ytPosts
  }

  /**
   * Yt投稿データをYt投稿ビューに変換する
   * @param {IYtPostData} ytPostData DBのYt投稿データ一覧
   * @returns {IYtPostView}
   */
  function setYtPostView(ytPostData: IYtPostData): IYtPostView {
    // let hashtagName: string[] = [];

    // // ハッシュタグがある場合、セット
    // if (ytPostData.hashtagName !== null) {
    //   hashtagName = ytPostData.hashtagName.split(',');
    // }

    // アカウントデータをセット
    const accountData: IPostAccountView = {
      accountId: ytPostData.accountId,
      screenName: ytPostData.accountScreenName,
      accountUrl: 'https://www.youtube.com/channel/' + ytPostData.accountId,
      pictureUrl: ytPostData.accountPictureUrl,
      followers: ytPostData.accountFollowers,
      postDateFollowers: ytPostData.postDateFollowers,
      categoryName: ytPostData.accountCategoryName,
      subCategoryName: ytPostData.accountSubCategoryName,
      snsType: 'youtube',
      dataFlg: ytPostData.dataFlg,
      hiddenSubscribersFlg: ytPostData.hiddenSubscribersFlg,
    }

    // 投稿のデータをセット
    const ytPostView: IYtPostView = {
      postId: ytPostData.ytPostId,
      account: accountData,
      postUrl: 'https://www.youtube.com/watch?v=' + ytPostData.ytPostId,
      publishDate: moment(ytPostData.publishDate).format('ll HH:mm'),
      publishDateUnix: moment(ytPostData.publishDate).unix(),
      title: ytPostData.title,
      message: ytPostData.description,
      picture: [
        {
          url: ytPostData.thumbnailUrl,
        },
      ],
      views: ytPostData.views,
      likes: ytPostData.likes,
      dislikes: ytPostData.dislikes,
      comments: ytPostData.comments,
      duration: ytPostData.duration,
      tags: {
        globalTagIds: ytPostData.globalTagIds,
        globalTagNames: ytPostData.globalTagNames,
        groupTagIds: ytPostData.groupTagIds,
        groupTagNames: ytPostData.groupTagNames,
        userTagIds: ytPostData.userTagIds,
        userTagNames: ytPostData.userTagNames,
      },
      memoCount: ytPostData.memoCount,
    }

    return ytPostView
  }

  return {
    setYtPosts: setYtPosts,
  }
}

/**
 * Lineの投稿のデータセットに関するサービス
 * @return {Object} オブジェクト
 */
function LinePostSetData() {
  /**
   * Line投稿のデータをセット
   * @param {Object} linePostDatas 投稿一覧
   * @return {Object} 投稿一覧
   */
  function setLinePosts(linePostDatas: ILinePostData[]) {
    // データがない場合、リータン
    if (linePostDatas.length === 0) {
      return []
    }

    const linePosts: ILinePostView[] = []

    const postDatas: ILinePostData[] = linePostDatas

    // 投稿のデータをセット
    postDatas.forEach(function (linePost) {
      // 投稿一覧に投稿のデータを追加
      linePosts.push(setLinePostView(linePost))
    })

    return linePosts
  }

  /**
   * Line投稿データをLine投稿ビューに変換する
   * @param {ILinePostData} linePostData DBのLine投稿データ一覧
   * @returns {ILinePostView}
   */
  function setLinePostView(linePostData: ILinePostData): ILinePostView {
    // アカウントデータをセット
    const accountData: IPostAccountView = {
      accountId: linePostData.accountId,
      screenName: linePostData.accountScreenName,
      accountUrl: 'https://timeline.line.me/user/' + linePostData.accountId,
      pictureUrl: linePostData.accountPictureUrl,
      followers: linePostData.accountFollowers,
      postDateFollowers: linePostData.postDateFollowers,
      categoryName: linePostData.accountCategoryName,
      subCategoryName: linePostData.accountSubCategoryName,
      snsType: 'line',
      dataFlg: linePostData.dataFlg,
    }

    // 投稿のデータをセット
    const linePostView: ILinePostView = {
      postId: linePostData.linePostId,
      account: accountData,
      postUrl:
        'https://timeline.line.me/post/' + linePostData.accountId + '/' + linePostData.linePostId,
      publishDate: moment(linePostData.publishDate).format('ll HH:mm'),
      publishDateUnix: moment(linePostData.publishDate).unix(),
      totalMedia: linePostData.totalMedia,
      type: linePostData.type,
      message: linePostData.message,
      picture: [
        {
          url: linePostData.thumbnailUrl,
        },
      ],
      likes: linePostData.likes,
      comments: linePostData.comments,
      shares: linePostData.shares,
      engagements: linePostData.engagements,
      engagementRate: linePostData.engagementRate,
      tags: {
        globalTagIds: linePostData.globalTagIds,
        globalTagNames: linePostData.globalTagNames,
        groupTagIds: linePostData.groupTagIds,
        groupTagNames: linePostData.groupTagNames,
        userTagIds: linePostData.userTagIds,
        userTagNames: linePostData.userTagNames,
      },
      memoCount: linePostData.memoCount,
    }

    return linePostView
  }

  return {
    setLinePosts: setLinePosts,
  }
}

/**
 * TikTokの投稿のデータセットに関するサービス
 * @return {Object} オブジェクト
 */
function TikTokPostSetData() {
  /**
   * TikTok投稿のデータをセット
   * @param {Object} tiktokPostDatas 投稿一覧
   * @return {Object} 投稿一覧
   */
  function setTikTokPosts(tiktokPostDatas: ITikTokPostData[]) {
    // データがない場合、リータン
    if (!tiktokPostDatas) {
      return []
    }

    // 投稿のデータをセット
    const tiktokPosts: ITikTokPostView[] = tiktokPostDatas.map((tiktokPost) =>
      setTikTokPostView(tiktokPost)
    )

    return tiktokPosts
  }

  /**
   * TikTok投稿データをTikTok投稿ビューに変換する
   * @param {ITikTokPostData} tiktokPostData DBのTikTok投稿データ一覧
   * @returns {ITikTokPostView}
   */
  function setTikTokPostView(tiktokPostData: ITikTokPostData): ITikTokPostView {
    // アカウントデータをセット
    const accountData: IPostAccountView = {
      accountId: tiktokPostData.accountId,
      screenName: tiktokPostData.accountScreenName,
      accountUrl: 'https://www.tiktok.com/@' + tiktokPostData.accountUsername,
      pictureUrl: tiktokPostData.accountPictureUrl,
      followers: tiktokPostData.accountFollowers,
      postDateFollowers: tiktokPostData.postDateFollowers,
      categoryName: tiktokPostData.accountCategoryName,
      subCategoryName: tiktokPostData.accountSubCategoryName,
      snsType: 'tiktok',
      dataFlg: tiktokPostData.dataFlg,
    }

    // 投稿のデータをセット
    const tiktokPostView: ITikTokPostView = {
      postId: tiktokPostData.ttPostId,
      account: accountData,
      postUrl:
        'https://www.tiktok.com/@' +
        tiktokPostData.accountUsername +
        `/${tiktokPostData.type === 'carousel' ? 'photo' : 'video'}/` +
        tiktokPostData.ttPostId,
      publishDate: moment(tiktokPostData.publishDate).format('ll HH:mm'),
      publishDateUnix: moment(tiktokPostData.publishDate).unix(),
      totalMedia: tiktokPostData.totalMedia,
      type: tiktokPostData.type,
      message: tiktokPostData.message,
      picture: [
        {
          url: tiktokPostData.thumbnailUrl,
        },
      ],
      likes: tiktokPostData.likes,
      comments: tiktokPostData.comments,
      shares: tiktokPostData.shares,
      collects: tiktokPostData.collects,
      plays: tiktokPostData.plays,
      engagements: tiktokPostData.engagements,
      engagementRate: tiktokPostData.engagementRates,
      tags: {
        globalTagIds: tiktokPostData.globalTagIds,
        globalTagNames: tiktokPostData.globalTagNames,
        groupTagIds: tiktokPostData.groupTagIds,
        groupTagNames: tiktokPostData.groupTagNames,
        userTagIds: tiktokPostData.userTagIds,
        userTagNames: tiktokPostData.userTagNames,
      },
      music: {
        id: tiktokPostData.ttPostMusicId,
        title: tiktokPostData.musicTitle,
        authorName: tiktokPostData.musicAuthorName,
        thumbnailUrl: tiktokPostData.musicThumbnailUrl,
        url: `https://www.tiktok.com/music/${tiktokPostData.musicTitle}-${tiktokPostData.ttPostMusicId}`,
      },
      memoCount: tiktokPostData.memoCount,
    }

    return tiktokPostView
  }

  return {
    setTikTokPosts: setTikTokPosts,
  }
}

/**
 * PostSearchData フィルタリング処理に関するサービス
 * @return {Object} オブジェクト
 */
function PostSearchData($q, PostAction) {
  /**
   * getFilterLists フィルタリング対象の推薦を取得
   * @param {String} filterType フィルタリングのタイプ(投稿タグ OR アカウントタグ OR ハッシュタグ OR アカウント)
   * @param {string} snsType SNSタイプ
   * @param {Object} getOptions 投稿取得の要件
   * @return {Object} フィルタリング対象の推薦一覧
   */
  function getFilterLists(filterType, snsType, getOptions) {
    const deferred = $q.defer()
    let filterLists = null

    switch (filterType) {
      case 'getTag':
        filterLists = PostAction.tagLists
        getOptions.tagFilter = null
        break
      case 'getAccountTag':
        filterLists = PostAction.accountTagLists
        getOptions.accountTagFilter = null
        break
      case 'getAccount':
        filterLists = PostAction.accountLists
        getOptions.accountFilter = null
        break
    }

    // フィルタリング対象の推薦がある場合、リターン
    if (filterLists !== null) {
      deferred.resolve(filterLists)
    } else {
      // フィルタリング対象の推薦がない場合、取得
      PostAction.getCounts(filterType, snsType, getOptions).then((filterData) => {
        deferred.resolve(filterData)
      })
    }

    return deferred.promise
  }

  /**
   * createFilterFor フィルタリングの作成
   * @param {String} query クエリ
   * @return {Object} フィルタリスト
   */
  function createFilterFor(query: string) {
    const lowercaseQuery = query.toLowerCase()

    return function filterFn(postFilterList) {
      return postFilterList.value.toLowerCase().match(new RegExp(lowercaseQuery, 'i')) !== null
    }
  }

  /**
   * queryTagSearch タグを入力する時の処理
   * @param {String} filterValue 入力したバリュー
   * @param {String} filterType フィルタリングのタイプ(投稿タグ OR アカウントタグ OR ハッシュタグ OR アカウント)
   * @param {string} snsType SNSタイプ
   * @param {Object} getOptions 投稿取得の要件
   * @return {Object} フィルタリング結果
   */
  function queryFilterSearch(filterValue, filterType, snsType, getOptions) {
    const deferred = $q.defer()
    let results: {
      value: string
      count: number
    }[]

    // フィルタリング対象の推薦を取得
    getFilterLists(filterType, snsType, getOptions).then((filterData) => {
      results = filterValue ? filterData.filter(createFilterFor(filterValue)) : filterData
      deferred.resolve(results)
    })
    return deferred.promise
  }

  /**
   * 入力したワードでハッシュタグ一覧を取得
   * @param {String} hashtagSuggest 入力したワード
   * @param {string} snsType SNSタイプ
   * @param {Object} getOptions 投稿取得の要件
   * @return {Object} ハッシュタグ一覧
   */
  function getHashtagList(hashtagSuggest: string, snsType: string, getOptions) {
    const deferred = $q.defer()
    PostAction.hashtagFilterStack.push(1)
    // 1.5秒後に入力が止まってるかチェックしてハッシュタグ一覧取得を行う
    setTimeout(() => {
      PostAction.hashtagFilterStack.pop()
      if (PostAction.hashtagFilterStack.length !== 0) {
        deferred.resolve(PostAction.hashtagLists)
        return deferred.promise
      }
      PostAction.hashtagFilterStack = []

      const options = JSON.parse(JSON.stringify(getOptions))

      // ハッシュタグ一覧を取得
      options.hashtagFilter = null
      options.hashtagSuggest = hashtagSuggest

      PostAction.getCounts('getHashtag', snsType, options).then((hashtagList) => {
        deferred.resolve(hashtagList)
      })
    }, 1500)
    return deferred.promise
  }

  /**
   * getTagList タグリストを取得
   * @param {String} filterType フィルタリングのタイプ(投稿タグ OR アカウントタグ)
   * @param {string} snsType SNSタイプ
   * @param {Object} getOptions 投稿取得の要件
   * @return {Object} フィルタリング結果
   */
  function getTagList(filterType, snsType, getOptions) {
    const deferred = $q.defer()

    // フィルタリング対象の推薦を取得
    getFilterLists(filterType, snsType, getOptions).then((tags) => {
      deferred.resolve(tags)
    })

    return deferred.promise
  }

  return {
    queryFilterSearch: queryFilterSearch,
    getHashtagList: getHashtagList,
    getTagList: getTagList,
  }
}

/**
 * PostService 投稿表示機能関連のサービスを1つのサービスにオブジェクトとして格納
 * @param {Function} PostActions 投稿用サービス
 * @param {Function} PostSearchData フィルタリングのデータセットに関するService
 * @param {Function} FbPostSetData Faceebookの投稿のデータセットに関するService
 * @param {Function} TwPostSetData Twitterの投稿のデータセットに関するService
 * @param {Function} InPostSetData Instagramの投稿のデータセットに関するService
 * @param {Function} YtPostSetData Youtubeの投稿のデータセットに関するService
 * @param {Function} LinePostSetData Lineの投稿のデータセットに関するService
 * @param {Function} TikTokPostSetData TikTokの投稿のデータセットに関するService
 * @return {Object} サービスのオブジェクト
 */
function PostService(
  PostAction,
  PostSearchData,
  FbPostSetData,
  TwPostSetData,
  InPostSetData,
  YtPostSetData,
  LinePostSetData,
  TikTokPostSetData
) {
  return {
    Action: PostAction,
    Search: PostSearchData,
    FbPost: FbPostSetData,
    TwPost: TwPostSetData,
    InPost: InPostSetData,
    YtPost: YtPostSetData,
    LinePost: LinePostSetData,
    TikTokPost: TikTokPostSetData,
  }
}

// 投稿表示機能用のサービスを定義
app
  .factory('PostService', PostService)
  .factory('PostAction', PostAction)
  .factory('PostSearchData', PostSearchData)
  .factory('FbPostSetData', FbPostSetData)
  .factory('TwPostSetData', TwPostSetData)
  .factory('InPostSetData', InPostSetData)
  .factory('YtPostSetData', YtPostSetData)
  .factory('LinePostSetData', LinePostSetData)
  .factory('TikTokPostSetData', TikTokPostSetData)
